<template>
    <div class="w-full max-w-full h-full p-2 pt-2 flex flex-col items-start justify-start gap-6 overflow-y-auto overflow-x-hidden min-w-0">
        <div class="flex flex-row justify-between items-center w-full max-w-full min-w-0">
            <div class="flex flex-col gap-1 w-full min-w-0">
                  <div class="flex flex-col md:flex-row md:justify-between md:items-center w-full gap-2 min-w-0">
                     <h1 class="text-xl sm:text-2xl text-gray-700 dark:text-gray-200 flex flex-col pl-14 md:pl-0 min-w-0">
                        <span>{{ greetings }}</span>
                        <span class="text-gray-500 text-sm block sm:inline">{{ userStore.state.userInfo.name }}</span>
                     </h1>
                    
                     <div class="relative flex items-center gap-2 flex-wrap sm:flex-nowrap min-w-0">
                        <input 
                            data-tour="search-playlists"
                            v-model="searchQuery" 
                            type="text" 
                            :placeholder="$t('playlist.searchPlaylists')" 
                            class="pl-9 pr-4 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-colors duration-200 w-36 sm:w-48 sm:focus:w-64 shadow-sm hover:border-gray-300 dark:hover:border-gray-600"
                        >
                        <MagnifyingGlassIcon class="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                        <Tooltip position="left" :text="$t('tooltip.addPlaylist')" class="ml-2">
                            <button
                                type="button"
                                data-tour="create-playlist"
                                @click.stop="addPlaylist"
                                class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-green-50 dark:hover:bg-green-950/40 transition-colors focus:outline-none cursor-pointer"
                                :aria-label="$t('tooltip.addPlaylist')"
                            >
                                <PlusCircleIcon class="w-8 h-8 text-green-500 hover:text-green-600 transition-colors pointer-events-none" />
                            </button>
                        </Tooltip>
                        <Tooltip position="left" :text="$t('tour.startTour')" class="ml-1">
                            <button
                                type="button"
                                @click.stop="() => startDashboardTour(true)"
                                class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors focus:outline-none cursor-pointer"
                                :aria-label="$t('tour.startTour')"
                            >
                                <QuestionMarkCircleIcon class="w-8 h-8 text-indigo-500 hover:text-indigo-600 transition-colors pointer-events-none" />
                            </button>
                        </Tooltip>
                     </div>
                 </div>
            </div>
        </div>
        <div class="w-full h-full max-w-full flex flex-col items-center justify-center gap-6"
            v-if="status === 'loading'">
            <Load/>
        </div>
        <div class="w-full max-w-full flex flex-col gap-6" v-if="status === 'loaded'">
             <!-- Search Results -->
             <div class="w-full max-w-full" v-if="searchQuery">
                <h2 class="font-bold text-lg mb-2 text-gray-700">
                    {{ $t('dashboard.searchResults', { query: searchQuery }) }}
                </h2>
                <div v-if="searchingDb" class="flex items-center gap-4 overflow-x-auto pb-4">
                    <PlaylistCardSkeleton v-for="i in 3" :key="'search-skeleton-' + i" />
                </div>
                <div v-else-if="combinedSearchResults.length === 0" class="text-gray-500 italic">
                    {{ $t('playlist.noPlaylistsFound') }}
                </div>
                <PlaylistCardContainer v-else>
                    <PlaylistCardV2 
                        v-for="(playlist, index) in combinedSearchResults" 
                        :key="index" 
                        :playlist="playlist" 
                        class="snap-start"
                        :editVisible="isEditor(playlist)"
                        @edit="onEdit"
                        @favorite="onFavorite"
                        @play="onPlay"
                    />
                </PlaylistCardContainer>
            </div>

            <div class="w-full max-w-full flex flex-col gap-6" v-else>
            <div class="w-full max-w-full" v-if="playlists.length > 0" data-tour="highlighted-playlist">
                <HighlightedPlaylistCard :playlist="playlists[0]" 
                    :favoriteVisible="isEditor(playlists[0])"
                    :shareVisible="isEditor(playlists[0])"
                    :editVisible="isEditor(playlists[0])"
                    @edit="onEdit"
                    @favorite="onFavorite"
                    @share="onShare"
                    @play="onPlay"
                />
            </div>
            <div class="w-full max-w-full" v-if="favorites.length > 0">
                <h2 class="font-bold text-lg mb-2 text-gray-700 dark:text-gray-200">{{ $t('dashboard.favorites') }}</h2>
                <PlaylistCardContainer>
                    <PlaylistCardV2 
                        v-for="playlist in favorites" 
                        :key="'fav-' + getPlaylistId(playlist)" 
                        :playlist="playlist" 
                        class="snap-start"
                        :editVisible="isEditor(playlist)"
                        @edit="onEdit"
                        @favorite="onFavorite"
                        @play="onPlay"
                    />
                </PlaylistCardContainer>
            </div>

            <!-- Upcoming Events Section (Next 3 events the user is participating in) -->
            <div class="w-full max-w-full">
                <div class="flex items-center justify-between mb-3">
                    <h2 class="font-bold text-lg text-gray-900 dark:text-white">
                        {{ $t('dashboard.upcomingEvents') }}
                    </h2>
                    <button 
                        @click="goToSchedule"
                        class="text-xs sm:text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                        {{ $t('dashboard.viewSchedule') }}
                    </button>
                </div>

                <!-- Loading skeleton -->
                <div v-if="loadingEvents" class="flex items-center gap-4 overflow-x-auto pb-4 no-scrollbar">
                    <div 
                        v-for="i in 3" 
                        :key="'event-skel-' + i" 
                        class="w-80 sm:w-[340px] h-32 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/60 p-4 shrink-0 flex flex-col justify-between shadow-xs"
                    >
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse shrink-0"></div>
                            <div class="flex-1 space-y-2">
                                <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4 animate-pulse"></div>
                                <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
                            </div>
                        </div>
                        <div class="flex items-center justify-between pt-2">
                            <div class="h-6 w-24 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse"></div>
                            <div class="h-7 w-28 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <!-- Friendly Empty State -->
                <div 
                    v-else-if="upcomingEvents.length === 0" 
                    class="w-full py-6 px-4 sm:px-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4"
                >
                    <div class="flex items-center gap-3.5 text-center sm:text-left">
                        <div class="p-3 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs shrink-0">
                            <SparklesIcon class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                                {{ $t('dashboard.noUpcomingEvents') }}
                            </h3>
                            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 max-w-lg">
                                {{ $t('dashboard.noUpcomingEventsDesc') }}
                            </p>
                        </div>
                    </div>
                    <button 
                        @click="goToSchedule"
                        class="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-gray-800 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-650 transition-colors shadow-2xs cursor-pointer border shrink-0 flex items-center gap-1.5"
                    >
                        <CalendarDaysIcon class="w-4 h-4 text-indigo-500" />
                        <span>{{ $t('dashboard.viewSchedule') }}</span>
                    </button>
                </div>

                <!-- Event Cards Container -->
                <PlaylistCardContainer v-else>
                    <DashboardEventCard 
                        v-for="event in upcomingEvents"
                        :key="event.id || event._id"
                        :event="event"
                        :categories="categories"
                        class="snap-start"
                        @click="onSelectEvent"
                    />
                </PlaylistCardContainer>
            </div>

            <!-- Filter Bar -->
            <div class="w-full max-w-full flex items-center gap-2 sm:gap-3 pt-2 pb-2 overflow-x-auto no-scrollbar scroll-smooth">
                <div class="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div class="relative flex items-center shrink-0" v-if="displayedTags.length > 0">
                        <input 
                            v-model="tagSearchInput"
                            @keydown.enter="addTagFilter(tagSearchInput)"
                            type="text"
                            :placeholder="$t('dashboard.searchTags')"
                            class="pl-8 pr-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all w-28 sm:w-32 sm:focus:w-48"
                        />
                        <MagnifyingGlassIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 absolute left-2.5 pointer-events-none" />
                    </div>
                    <button 
                        @click="setFilter('All')"
                        class="px-3.5 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap"
                        :class="!showOfflineOnly && activeFilter.length === 0 ? 'bg-gray-900 dark:bg-gray-900 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'"
                    >
                        {{ $t('dashboard.filterAll') }}
                    </button>
                    <!-- Downloaded / Offline Filter Button -->
                    <button 
                        @click="toggleOfflineFilter"
                        class="px-3 sm:px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 sm:gap-2 cursor-pointer border-none"
                        :class="showOfflineOnly ? 'bg-emerald-600 dark:bg-emerald-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-600'"
                        :title="$t('dashboard.downloadedPlaylistsTitle')"
                    >
                        <ArrowDownTrayIcon class="w-4 h-4 shrink-0" />
                        <span>{{ $t('dashboard.downloadedFilter') }}</span>
                        <span 
                            class="text-xs px-1.5 py-0.5 rounded-full font-bold"
                            :class="showOfflineOnly ? 'bg-white/25 text-white' : 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300'"
                        >
                            {{ downloadedCount }}
                        </span>
                    </button>
                </div>
                <div class="flex items-center gap-2 sm:gap-3 shrink-0" v-if="displayedTags.length > 0">
                    <button 
                        v-for="tag in displayedTags" 
                        :key="tag"
                        @click="setFilter(tag)"
                        class="px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap"
                        :class="!showOfflineOnly && activeFilter.includes(tag) ? 'bg-gray-900 dark:bg-gray-900 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'"
                    >
                        {{ tag }}
                    </button>
                </div>
            </div>

            <!-- Downloaded Hub View (When showOfflineOnly is true) -->
            <div class="w-full max-w-full flex flex-col gap-4" v-if="showOfflineOnly">
                <!-- Updates Available Hub Banner -->
                <div v-if="outdatedCount > 0" class="w-full bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-950/40 dark:via-amber-900/20 dark:to-transparent border border-amber-300 dark:border-amber-700/60 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm animate-fade-in">
                    <div class="flex items-start sm:items-center gap-3.5">
                        <div class="p-2.5 bg-amber-500 text-white rounded-xl shadow-sm shrink-0">
                            <ArrowPathRoundedSquareIcon class="w-6 h-6" />
                        </div>
                        <div class="flex flex-col">
                            <h2 class="font-bold text-base sm:text-lg text-amber-900 dark:text-amber-200">
                                {{ $t('dashboard.updateAvailable') }}
                            </h2>
                            <p class="text-xs sm:text-sm text-amber-800 dark:text-amber-300 mt-0.5">
                                {{ $t('dashboard.updatesAvailableBanner', { count: outdatedCount }) }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 self-end sm:self-auto shrink-0">
                        <button 
                            @click="handleSyncAll"
                            :disabled="isSyncingAll"
                            class="flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 active:bg-amber-800 transition-colors shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border-none"
                        >
                            <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isSyncingAll }" />
                            <span>{{ isSyncingAll ? $t('dashboard.syncing') : $t('dashboard.syncAll') }}</span>
                        </button>
                    </div>
                </div>

                <!-- Storage Stats Banner -->
                <div class="w-full bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-slate-800 dark:to-slate-800/80 border border-emerald-200/70 dark:border-slate-700 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                    <div class="flex items-start sm:items-center gap-3.5">
                        <div class="p-2.5 bg-emerald-600 text-white rounded-xl shadow-sm shrink-0">
                            <ArrowDownTrayIcon class="w-6 h-6" />
                        </div>
                        <div class="flex flex-col">
                            <h2 class="font-bold text-base sm:text-lg text-gray-900 dark:text-white">
                                {{ $t('dashboard.downloadedPlaylistsTitle') }}
                            </h2>
                            <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                                {{ $t('dashboard.offlineStorageInfo', { size: storageUsage.formattedUsage }) }} 
                                <span class="mx-1">•</span> 
                                {{ $t('dashboard.offlineStorageDetails', { playlists: storageUsage.playlistCount, songs: storageUsage.songCount }) }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 self-end sm:self-auto shrink-0">
                        <button 
                            v-if="downloadedPlaylists.length > 0"
                            @click="handleClearAllOffline"
                            :disabled="isClearingOffline"
                            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors cursor-pointer"
                        >
                            <TrashIcon class="w-4 h-4 shrink-0" />
                            <span>{{ $t('dashboard.clearAllOffline') }}</span>
                        </button>
                    </div>
                </div>

                <!-- Downloaded Playlists List -->
                <div v-if="downloadedPlaylists.length === 0" class="w-full py-12 flex flex-col items-center justify-center text-center gap-2 bg-gray-50/60 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                    <CloudArrowDownIcon class="w-10 h-10 text-gray-400" />
                    <p class="font-medium text-gray-700 dark:text-gray-300 text-sm">
                        {{ $t('dashboard.noDownloadedPlaylists') }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 max-w-sm">
                        {{ $t('dashboard.noDownloadedPlaylistsDesc') }}
                    </p>
                </div>
                <PlaylistCardContainer v-else>
                    <PlaylistCardV2 
                        v-for="playlist in downloadedPlaylists" 
                        :key="'offline-' + getPlaylistId(playlist)" 
                        :playlist="playlist" 
                        class="snap-start"
                        :editVisible="isEditor(playlist)"
                        @edit="onEdit"
                        @favorite="onFavorite"
                        @play="onPlay"
                    />
                </PlaylistCardContainer>
            </div>

            <!-- Filtered Results (When activeFilter has tags and not showing offline only) -->
            <div class="w-full max-w-full" v-else-if="activeFilter.length > 0">
                <h2 class="font-bold text-lg mb-2 text-gray-700">
                    {{ $t('dashboard.filteredBy', { tags: activeFilter.join(', ') }) }}
                </h2>
                <div v-if="filteredPlaylists.length === 0" class="text-gray-500 italic mt-4">
                    {{ $t('playlist.noPlaylistsFound') }}
                </div>
                <PlaylistCardContainer v-else>
                    <PlaylistCardV2 
                        v-for="(playlist, index) in filteredPlaylists" 
                        :key="index" 
                        :playlist="playlist" 
                        class="snap-start"
                        :editVisible="isEditor(playlist)"
                        @edit="onEdit"
                        @favorite="onFavorite"
                        @play="onPlay"
                    />
                </PlaylistCardContainer>
            </div>

            <!-- All Playlists (Default View) -->
            <div class="w-full max-w-full" v-else-if="otherPlaylists.length > 0 && activeFilter.length === 0 && !showOfflineOnly">
                <h2 class="font-bold text-lg mb-2 text-gray-700">{{ $t('dashboard.allPlaylists') }}</h2>
                <PlaylistCardContainer :hasMore="hasMore" @load-more="onLoadMore">
                    <PlaylistCardV2 
                        v-for="playlist in otherPlaylists" 
                        :key="getPlaylistId(playlist)" 
                        :playlist="playlist" 
                        class="snap-start"
                        :editVisible="isEditor(playlist)"
                        @edit="onEdit"
                        @favorite="onFavorite"
                        @play="onPlay"
                    />
                    <template v-if="loadingMore">
                        <PlaylistCardSkeleton v-for="i in 3" :key="'skeleton-' + i" />
                    </template>
                </PlaylistCardContainer>
            </div>
          </div>   
        </div>
        <div class="w-full h-full max-w-full flex flex-col items-center justify-center p-4 sm:p-6 my-auto" 
            v-if="status === 'empty'">
            <div class="w-full max-w-md bg-white dark:bg-gray-800 rounded-3xl p-5 sm:p-6 shadow-2xl border border-gray-100 dark:border-gray-700/70 transition-all">
                <!-- Top Badge Banner -->
                <div class="w-full bg-[#FFF4EC] dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/40 rounded-xl py-2 px-4 text-center text-xs sm:text-sm font-semibold text-[#9A5B2F] dark:text-amber-200 shadow-xs">
                    {{ $t('dashboard.guidedJourney.startJourney') }} 🚀
                </div>

                <!-- Guided Container -->
                <div class="bg-[#F8F9FB] dark:bg-gray-900/40 rounded-2xl p-4 sm:p-5 mt-3 border border-gray-100/80 dark:border-gray-800">
                    <!-- Step Progress Title -->
                    <h3 class="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                        {{ $t('dashboard.guidedJourney.stepProgress', { current: activeGuidedStep, total: 3, title: currentStepObj.short }) }}
                    </h3>

                    <!-- Progress Bars -->
                    <div class="flex items-center gap-2 mt-2.5 mb-5">
                        <div 
                            v-for="s in [1, 2, 3]" 
                            :key="'step-bar-' + s"
                            class="h-1.5 flex-1 rounded-full transition-all duration-300"
                            :class="activeGuidedStep >= s ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'"
                        />
                    </div>

                    <!-- Steps Timeline -->
                    <div class="space-y-1">
                        <div 
                            v-for="(st, idx) in guidedSteps" 
                            :key="'guided-step-' + st.step"
                            class="relative"
                        >
                            <!-- Step Header (Dot + Label) -->
                            <button
                                type="button"
                                @click="activeGuidedStep = st.step"
                                class="flex items-center gap-3 w-full text-left py-1.5 group cursor-pointer focus:outline-none"
                            >
                                <span 
                                    class="w-3.5 h-3.5 rounded-full transition-all duration-200 shrink-0"
                                    :class="activeGuidedStep === st.step 
                                        ? 'bg-indigo-600 dark:bg-indigo-500 ring-4 ring-indigo-100 dark:ring-indigo-950/60' 
                                        : activeGuidedStep > st.step 
                                            ? 'bg-indigo-600 dark:bg-indigo-500' 
                                            : 'bg-gray-300 dark:bg-gray-600 group-hover:bg-gray-400'"
                                />
                                <span 
                                    class="text-xs sm:text-sm transition-colors"
                                    :class="activeGuidedStep === st.step 
                                        ? 'font-bold text-gray-900 dark:text-white' 
                                        : 'font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'"
                                >
                                    {{ st.step }}. {{ st.short }}
                                </span>
                            </button>

                            <!-- Vertical connecting line to next step -->
                            <div 
                                v-if="idx < guidedSteps.length - 1"
                                class="absolute left-[6px] top-[24px] bottom-[-4px] w-0 transition-colors"
                                :class="[
                                    activeGuidedStep > st.step 
                                        ? 'border-l-2 border-indigo-600 dark:border-indigo-500' 
                                        : activeGuidedStep === st.step
                                            ? 'border-l-2 border-indigo-500 dark:border-indigo-400'
                                            : 'border-l-2 border-dotted border-gray-300 dark:border-gray-600'
                                ]"
                            />

                            <!-- Active Step Expanded Card -->
                            <div 
                                v-if="activeGuidedStep === st.step"
                                class="ml-6 my-2 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700 shadow-sm relative z-10 animate-fade-in"
                            >
                                <h4 class="font-bold text-sm text-gray-900 dark:text-white">
                                    {{ st.step }}. {{ st.title }}
                                </h4>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3.5 leading-relaxed">
                                    {{ st.desc }}
                                </p>
                                <button
                                    type="button"
                                    :data-tour="st.dataTour"
                                    @click.stop="st.action"
                                    class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-95 text-white text-xs sm:text-sm font-semibold shadow-sm hover:shadow transition-all cursor-pointer"
                                >
                                    <PlusIcon class="w-4 h-4 stroke-[2.5]" />
                                    <span>{{ st.actionText }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ModalShare/>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineProps, watch, defineEmits } from 'vue'
import PlaylistCardV2 from '@/components/playlist/PlaylistCardV2.vue'
import DashboardEventCard from '@/components/schedule/DashboardEventCard.vue'
import PlaylistCardContainer from '@/components/playlist/PlaylistCardContainer.vue'
import PlaylistCardSkeleton from '@/components/playlist/PlaylistCardSkeleton.vue'
import userStore from '@/store/user.store'
import PlaylistService from '@/service/playlist.service';
import HighlightedPlaylistCard from '@/components/playlist/HighlightedPlaylistCard.vue';
import Load from '@/components/TriadLoad.vue';
import { useRouter, useRoute } from 'vue-router';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import shareModalStore from '@/store/share.modal.store';
import searchModalStore from '@/store/search.modal.store';
import Tooltip from '@/components/common/elements/TriadTooltip.vue';
import { 
    PlusCircleIcon, 
    PlusIcon,
    MagnifyingGlassIcon, 
    ArrowDownTrayIcon, 
    CloudArrowDownIcon, 
    TrashIcon,
    ArrowPathRoundedSquareIcon,
    ArrowPathIcon,
    QuestionMarkCircleIcon,
    CalendarDaysIcon,
    SparklesIcon
} from '@heroicons/vue/24/outline';
import debounce from 'lodash.debounce'
import { useI18n } from 'vue-i18n'
import offlinePlaylistService from '@/service/offlinePlaylist.service'
import offlineScheduleService from '@/service/offlineSchedule.service'
import dbService from '@/service/db.service'
import { appEvents } from '@/store/app.events'
import { useTour } from '@/composables/useTour'

const { t } = useI18n()
const { startDashboardTour } = useTour()

const props = defineProps({
    playlists: {
        type: Array,
        required: true
    },
    status: {
        type: String,
        required: true,
        default: 'loading'
    },
    hasMore: {
        type: Boolean,
        default: false
    },
    loadingMore: {
        type: Boolean,
        default: false
    },
    favoriteIds: {
        type: Object,
        default: () => new Set()
    }
})

const emit = defineEmits(['load-more'])

const onLoadMore = () => {
    emit('load-more')
}

const router = useRouter()
const route = useRoute()
const playlists = ref(props.playlists);
const favorites = ref([]);
const playlistByTag = ref({});
const activeFilter = ref([]);
const tagSearchInput = ref('');
const searchQuery = ref(''); // Added searchQuery ref
const dbSearchResults = ref([]);
const searchingDb = ref(false);

// Upcoming Events State
const upcomingEvents = ref([]);
const categories = ref([]);
const loadingEvents = ref(false);

const goToSchedule = () => {
    router.push('/schedule');
};

const onSelectEvent = (event) => {
    const eventId = event?.id || event?._id;
    router.push({
        path: '/schedule',
        query: eventId ? { eventId } : {}
    });
};

const fetchCategories = async () => {
    if (!userStore.state.userInfo?.id) return;
    try {
        const response = await fetch('/api/schedule/categories', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
            const data = await response.json();
            categories.value = Array.isArray(data) ? data : [];
        }
    } catch (err) {
        console.warn('[DashboardPlaylist] Offline fallback for categories', err);
    }
};

const fetchUpcomingEvents = async () => {
    if (!userStore.state.userInfo?.id) return;
    loadingEvents.value = true;
    try {
        let allEvents = [];
        if (!navigator.onLine) {
            allEvents = await offlineScheduleService.getCachedEvents();
        } else {
            const response = await fetch('/api/schedule/events/user', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const responseData = await response.json();
                allEvents = responseData.data || responseData || [];
            } else if (response.status !== 401) {
                console.warn('[DashboardPlaylist] Failed to fetch events:', response.status);
            }
        }

        // Filter events that occur from today onwards and sort chronologically
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();

        const upcoming = (Array.isArray(allEvents) ? allEvents : [])
            .filter(event => {
                const eventStartTime = new Date(event.startTime || event.date || event.scheduledDate).getTime();
                const eventEndTime = event.endTime ? new Date(event.endTime).getTime() : eventStartTime;
                return eventEndTime >= startOfToday;
            })
            .sort((a, b) => {
                const timeA = new Date(a.startTime || a.date || a.scheduledDate).getTime();
                const timeB = new Date(b.startTime || b.date || b.scheduledDate).getTime();
                return timeA - timeB;
            });

        // Top 3 next events
        upcomingEvents.value = upcoming.slice(0, 3);
    } catch (err) {
        console.warn('[DashboardPlaylist] Error loading upcoming events:', err);
    } finally {
        loadingEvents.value = false;
    }
};

// Offline Downloaded Hub State
const showOfflineOnly = ref(false);
const downloadedPlaylists = ref([]);
const downloadedCount = computed(() => downloadedPlaylists.value.length);
const outdatedPlaylists = ref(new Map());
const outdatedCount = computed(() => outdatedPlaylists.value.size);
const isSyncingAll = ref(false);
const storageUsage = ref({
    usageBytes: 0,
    formattedUsage: '0 B',
    playlistCount: 0,
    songCount: 0
});
const isClearingOffline = ref(false);

const getPlaylistKeys = (p) => {
    if (!p) return [];
    const keys = [];
    if (p._id) keys.push(String(p._id));
    if (p.id) keys.push(String(p.id));
    if (p.uuid) keys.push(String(p.uuid));
    return keys;
};

const arePlaylistsEqual = (p1, p2) => {
    if (!p1 || !p2) return false;
    const k1 = getPlaylistKeys(p1);
    const k2 = getPlaylistKeys(p2);
    if (k1.some(k => k2.includes(k))) return true;
    if (p1.name && p2.name && p1.name.trim().toLowerCase() === p2.name.trim().toLowerCase()) return true;
    return false;
};

const deduplicatePlaylists = (playlistArray) => {
    const result = [];
    (playlistArray || []).forEach(p => {
        if (!p) return;
        const exists = result.some(item => arePlaylistsEqual(item, p));
        if (!exists) {
            result.push(p);
        }
    });
    return result;
};

const getPlaylistId = (p) => String(p?._id || p?.id || p?.uuid || p?.name || '');

const processPlaylists = () => {
    favorites.value = deduplicatePlaylists(playlists.value.filter(playlist => playlist.isFavorite));

    playlistByTag.value = playlists.value.reduce((acc, playlist) => {
        if (!playlist?.tags || playlist.tags.length === 0) return acc;
        playlist.tags.forEach(tag => {
            const tagName = typeof tag === 'object' && tag !== null ? tag.name : tag;
            if (!tagName) return;
            if (!acc[tagName]) acc[tagName] = [];
            if (!acc[tagName].some(p => arePlaylistsEqual(p, playlist))) {
                acc[tagName].push(playlist);
            }
        });
        return acc;
    }, {});

    if (playlists.value.length > 0) {
        status.value = 'loaded';
    }
};

const checkUpdates = async () => {
    if (!navigator.onLine) return;
    try {
        const results = await offlinePlaylistService.checkAllDownloadedUpdates(playlists.value);
        outdatedPlaylists.value = results;
    } catch (e) {
        console.warn('[DashboardPlaylist] Error checking updates:', e);
    }
};

const refreshDownloadedPlaylists = async () => {
    try {
        const offlineRegistry = await offlinePlaylistService.getRegistry();
        const downloadedIds = new Set(
            Object.values(offlineRegistry)
                .filter(item => item.status === 'downloaded')
                .map(item => String(item.id || item._id))
        );

        // Find matches in props.playlists, or build objects from cache/registry
        const found = [];
        const seenIds = new Set();

        (playlists.value || []).forEach(p => {
            const id = getPlaylistId(p);
            if (downloadedIds.has(id)) {
                found.push(p);
                seenIds.add(id);
            }
        });

        // If any offline playlist is not in playlists.value (e.g. cached offline while offline or before full list loaded)
        for (const item of Object.values(offlineRegistry)) {
            const id = String(item.id || item._id);
            if (item.status === 'downloaded' && !seenIds.has(id)) {
                // Try reading cached full playlist to get real cover, tags, and metadata
                const cacheKey = PlaylistService.PLAYLIST_CACHE_KEY.replace('[id]', id);
                let cachedData = null;
                try {
                    if (window.cacheInstance) {
                        cachedData = window.cacheInstance.getItem(cacheKey);
                    }
                    if (!cachedData) {
                        cachedData = await dbService.getItem(cacheKey);
                    }
                } catch (err) {
                    console.warn(`[DashboardPlaylist] Error reading cached playlist for ${id}:`, err);
                }

                if (cachedData) {
                    found.push({
                        ...cachedData,
                        _id: id,
                        id: id,
                        isFavorite: props.favoriteIds?.has ? (props.favoriteIds.has(id) || cachedData.isFavorite) : !!cachedData.isFavorite
                    });
                } else {
                    found.push({
                        _id: id,
                        id: id,
                        name: item.name,
                        description: item.description || `${item.songCount || 0} músicas salvas offline`,
                        cover: item.cover || null,
                        tags: Array.isArray(item.tags) ? item.tags : [],
                        songs: Array(item.songCount || 0).fill({}),
                        isFavorite: props.favoriteIds?.has ? props.favoriteIds.has(id) : false,
                        permissions: ['editor']
                    });
                }
                seenIds.add(id);
            }
        }

        downloadedPlaylists.value = found;
        storageUsage.value = await offlinePlaylistService.getStorageUsage();

        // Check for updates if online
        if (navigator.onLine) {
            checkUpdates();
        }
    } catch (e) {
        console.warn('[DashboardPlaylist] Error refreshing downloaded playlists:', e);
    }
};

const performDbSearch = debounce(async (query) => {
    if (!query) {
        dbSearchResults.value = [];
        return;
    }
    if (searchResults.value.length > 0) {
        dbSearchResults.value = [];
        return;
    }
    searchingDb.value = true;
    try {
        const res = await PlaylistService.fetchPaginated({ page: 1, size: 10, search: query });
        const results = res?.data || [];
        results.forEach(p => {
            p.isFavorite = props.favoriteIds.has(p.id) || props.favoriteIds.has(p._id);
        });
        dbSearchResults.value = results;
    } catch (e) {
        console.error("DB Search failed:", e);
        dbSearchResults.value = [];
    } finally {
        searchingDb.value = false;
    }
}, 300);

watch(searchQuery, (newVal) => {
    performDbSearch(newVal);
});

const isEditor = (playlist) => {
    return playlist.permissions && playlist.permissions.includes('editor')
}
const topTags = computed(() => {
    return Object.entries(playlistByTag.value)
        .sort(([, a], [, b]) => b.length - a.length)
        .slice(0, 5)
        .map(([tag]) => tag);
});

const displayedTags = computed(() => {
    const tags = new Set([...topTags.value, ...activeFilter.value]);
    return Array.from(tags);
});

const addTagFilter = (tag) => {
    if (!tag || tag.trim() === '') return;
    const cleanTag = tag.trim();
    const cleanLower = cleanTag.toLowerCase();
    let tagToAdd = cleanTag;
    const existingTag = Object.keys(playlistByTag.value).find(t => t.toLowerCase() === cleanLower);
    if (existingTag) {
        tagToAdd = existingTag;
    }
    if (!activeFilter.value.some(f => f.toLowerCase() === cleanLower)) {
        activeFilter.value.push(tagToAdd);
    }
    tagSearchInput.value = '';
}

const filteredPlaylists = computed(() => {
    if (activeFilter.value.length === 0) return [];
    const activeLower = activeFilter.value.map(f => f.toLowerCase());
    return playlists.value.filter(playlist => {
        if (!playlist.tags) return false;
        return playlist.tags.some(tag => {
            const tagName = typeof tag === 'object' && tag !== null ? tag.name : tag;
            if (!tagName) return false;
            return activeLower.includes(tagName.toLowerCase());
        });
    });
});

const otherPlaylists = computed(() => {
    if (playlists.value.length === 0) return [];
    // Exclude playlists[0] (shown in top Highlighted Card banner)
    const list = playlists.value.slice(1);
    // Exclude playlists already displayed in the Favorites row
    return list.filter(p => !favorites.value.some(fav => arePlaylistsEqual(fav, p)));
});

// Added searchResults computed property
const searchResults = computed(() => {
    if (!searchQuery.value) return [];
    const query = searchQuery.value.toLowerCase();
    return playlists.value.filter(playlist => 
        playlist.name.toLowerCase().includes(query)
    );
});

const combinedSearchResults = computed(() => {
    if (searchResults.value.length > 0) {
        return searchResults.value;
    }
    return dbSearchResults.value;
});

const greetings = computed(() => {
    const date = new Date()
    const hour = date.getHours()
    if (hour < 12) return t('dashboard.greetings.morning')
    if (hour < 18) return t('dashboard.greetings.afternoon')
    return t('dashboard.greetings.evening')
})
const status = ref(props.status === 'loaded' || (props.playlists && props.playlists.length > 0) ? 'loaded' : (props.status || 'loading'))

const FILTER_STORAGE_KEY = 'triad_dashboard_filter_tags';

onMounted(async () => {
    try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed && Array.isArray(parsed.tags)) {
                activeFilter.value = parsed.tags;
            }
        }
    } catch (e) {
        console.error('Error loading filter tags from local storage', e);
    }
    processPlaylists();
    await refreshDownloadedPlaylists();
    await fetchCategories();
    await fetchUpcomingEvents();

    if (route.query?.filter === 'downloaded') {
        showOfflineOnly.value = true;
        activeFilter.value = [];
    }

    appEvents.on('schedule:refresh', fetchUpcomingEvents);
    appEvents.on('offline:schedule-downloaded', fetchUpcomingEvents);
    appEvents.on('offline:schedule-cleared', fetchUpcomingEvents);
    appEvents.on('offline:registry-updated', refreshDownloadedPlaylists);
    appEvents.on('offline:playlist-downloaded', refreshDownloadedPlaylists);
    appEvents.on('offline:playlist-removed', refreshDownloadedPlaylists);
    appEvents.on('offline:all-cleared', refreshDownloadedPlaylists);
    appEvents.on('offline:playlist-synced', refreshDownloadedPlaylists);
    appEvents.on('offline:all-synced', refreshDownloadedPlaylists);
    window.addEventListener('online', checkUpdates);
});

onUnmounted(() => {
    appEvents.off('schedule:refresh', fetchUpcomingEvents);
    appEvents.off('offline:schedule-downloaded', fetchUpcomingEvents);
    appEvents.off('offline:schedule-cleared', fetchUpcomingEvents);
    appEvents.off('offline:registry-updated', refreshDownloadedPlaylists);
    appEvents.off('offline:playlist-downloaded', refreshDownloadedPlaylists);
    appEvents.off('offline:playlist-removed', refreshDownloadedPlaylists);
    appEvents.off('offline:all-cleared', refreshDownloadedPlaylists);
    appEvents.off('offline:playlist-synced', refreshDownloadedPlaylists);
    appEvents.off('offline:all-synced', refreshDownloadedPlaylists);
    window.removeEventListener('online', checkUpdates);
});

watch(() => route.query?.filter, (newFilter) => {
    if (newFilter === 'downloaded') {
        showOfflineOnly.value = true;
        activeFilter.value = [];
        refreshDownloadedPlaylists();
    }
});


watch(() => userStore.state.userInfo?.id, async (newId) => {
    if (newId) {
        await fetchCategories();
        await fetchUpcomingEvents();
    }
});

watch(() => props.playlists, async (newVal) => {
    playlists.value = deduplicatePlaylists(newVal || []);
    processPlaylists();
    await refreshDownloadedPlaylists();
}, { immediate: true, deep: true });

watch(() => props.status, (newStatus) => {
    status.value = newStatus;
});

const onCreate = () => {
    router.push('/playlist/new')
}

const openSearchModal = () => {
    searchModalStore.dispatch("open");
}

const activeGuidedStep = ref(1);

const guidedSteps = computed(() => [
    {
        step: 1,
        short: t('dashboard.guidedJourney.step1.short'),
        title: t('dashboard.guidedJourney.step1.title'),
        desc: t('dashboard.guidedJourney.step1.desc'),
        actionText: t('dashboard.guidedJourney.step1.action'),
        action: onCreate,
        dataTour: 'empty-create-playlist'
    },
    {
        step: 2,
        short: t('dashboard.guidedJourney.step2.short'),
        title: t('dashboard.guidedJourney.step2.title'),
        desc: t('dashboard.guidedJourney.step2.desc'),
        actionText: t('dashboard.guidedJourney.step2.action'),
        action: openSearchModal,
        dataTour: null
    },
    {
        step: 3,
        short: t('dashboard.guidedJourney.step3.short'),
        title: t('dashboard.guidedJourney.step3.title'),
        desc: t('dashboard.guidedJourney.step3.desc'),
        actionText: t('dashboard.guidedJourney.step3.action'),
        action: () => router.push('/team'),
        dataTour: null
    }
]);

const currentStepObj = computed(() => {
    return guidedSteps.value.find(s => s.step === activeGuidedStep.value) || guidedSteps.value[0];
});



const handleSyncAll = async () => {
    if (isSyncingAll.value || outdatedCount.value === 0) return;
    isSyncingAll.value = true;
    try {
        const ids = Array.from(outdatedPlaylists.value.keys());
        await offlinePlaylistService.syncAllDownloadedPlaylists(ids);
        outdatedPlaylists.value.clear();
        await refreshDownloadedPlaylists();
        toastStore.dispatch('addMessage', {
            message: t('dashboard.syncSuccess', { name: `${ids.length} playlists` }) || 'Playlists sincronizadas com sucesso!',
            icon: 'success'
        });
    } catch (e) {
        console.error('[DashboardPlaylist] Error syncing all playlists:', e);
        toastStore.dispatch('addMessage', {
            message: 'Erro ao sincronizar playlists offline',
            icon: 'error'
        });
    } finally {
        isSyncingAll.value = false;
    }
};

const toggleOfflineFilter = () => {
    showOfflineOnly.value = !showOfflineOnly.value;
    if (showOfflineOnly.value) {
        activeFilter.value = [];
        refreshDownloadedPlaylists();
    }
};

const handleClearAllOffline = async () => {
    const confirmed = await dialogStore.dispatch('confirm', {
        title: t('dashboard.clearAllOffline'),
        message: t('dashboard.clearAllConfirm')
    });
    if (!confirmed) return;

    isClearingOffline.value = true;
    try {
        await offlinePlaylistService.clearAllOfflineData();
        await refreshDownloadedPlaylists();
        toastStore.dispatch('addMessage', {
            message: t('dashboard.offlineClearedSuccess'),
            icon: 'success',
            ttl: 4000
        });
    } catch (e) {
        console.error('[DashboardPlaylist] Error clearing offline data:', e);
        toastStore.dispatch('addMessage', {
            message: 'Erro ao limpar dados offline',
            icon: 'error',
            ttl: 4000
        });
    } finally {
        isClearingOffline.value = false;
    }
};

const setFilter = (tag) => {
    showOfflineOnly.value = false;
    if (tag === 'All') {
        activeFilter.value = [];
        return;
    }
    
    const index = activeFilter.value.indexOf(tag);
    if (index === -1) {
        activeFilter.value.push(tag);
    } else {
        activeFilter.value.splice(index, 1);
    }
}

const onEdit = (playlist) => {
    router.push(`/playlist/${playlist._id || playlist.id}`)
}

const onFavorite = async (playlist) => {
    const playlistId = playlist._id || playlist.id;
    const response = await PlaylistService.toggleFavorite(playlistId)
    if (response.ok) {
        const data = await response.json()
        const isFav = data.isFavorite !== undefined ? data.isFavorite : (data.favorite !== undefined ? data.favorite : !playlist.isFavorite);
        playlist.isFavorite = isFav
        if (isFav) {
            props.favoriteIds.add(playlistId);
            if (!favorites.value.some(p => (p._id || p.id) === playlistId)) {
                favorites.value.push(playlist)
            }
        } else {
            props.favoriteIds.delete(playlistId);
            favorites.value = favorites.value.filter(p => (p._id || p.id) !== playlistId)
        }
    } else {
        toastStore.dispatch('addMessage', {
            message: 'Failed to toggle favorite',
            icon: 'error'
        });
    }
}

const onPlay = (playlist) => {
    const playlistId = playlist._id || playlist.id;
    if (playlistId && playlist.songs && playlist.songs.length > 0) {
        router.push(`/playlist/${playlistId}/song/${playlist.songs[0].song || playlist.songs[0].song_id}`)
    } else {
        router.push(`/playlist/${playlistId}`)
    }
}

const onShare = (playlist) => {
    shareModalStore.dispatch('open', playlist._id || playlist.id);
}

function addPlaylist() {
  router.push({path: '/playlist/new'})
}

watch(() => props.status, (newStatus) => {
    if (newStatus) {
        status.value = newStatus;
    }
}, { immediate: true })
</script>
